import React, { useRef } from 'react';
import { CircularProgress } from '@material-ui/core';
import translate from '../../locales';
import { useEffect, useState } from 'react';
import { styleWithCommonClasses } from '../../_constants/emparkConstants';

import TitleToolbar from '../Forms/TitleToolbar';
import { useExecution } from '../../hooks/useExecution';
import { createRateMapping, deleteRateMapping, getRateMappings, updateRateMapping } from '../../services/parametersService';
import ListItem from '../ListItem';
import ConfirmDialog from '../Dialogs/ConfirmDialog';
import LoadingDialog from '../Dialogs/LoadingDialog';
import { ratesService } from '../../services';
import RateMappingForm from '../Forms/Parameters/RateMappingForm';
import RateMappingPreview from '../Parameters/RateMappingPreview';
import { jsonDateStrToDisplay, toDateJsonString } from '../../_helpers/dateConvert';
import { connect } from 'react-redux';
import { contextsActions } from '../../actions/contextsActions';

const __t = translate;

const styles = styleWithCommonClasses({
    paddingOnTop: {
        paddingTop: '20px'
    },
    upperCase: {
        textTransform: 'uppercase'
    },
    containerSelect: {
        width: '100%',
        padding: '4px'
    },
    errorBorder: {
        border: '1px solid red',
        borderRadius: '5px'
    },
})

const RateMappingController = (props) => {
    const [rateMappings, setRateMappings] = useState([]);
    const [rates, setRates] = useState([]);
    const [error, setError] = useState(null);

    const fetchRateMappingsQuery = useExecution(getRateMappings);
    const fetchRatesQuery = useExecution(ratesService.getRates);

    useEffect(() => {
        fetchRateMappingsQuery.execute(props.contextId, props.parameterId)
            .then(data => {
                setRateMappings(data);
            })
            .catch(err => {
                console.error('Error fetching rate mappings:', err);
                setError('Unexpected Error');
            });

        fetchRatesQuery.execute(props.contextId)
            .then(data => {
                setRates(data.filter(r => r.productTypeCode == 'ROT' || r.productTypeCode == 'PRE'));
            })
            .catch(err => {
                console.error('Error fetching rates:', err);
                setError('Unexpected Error');
            });
    }, [props.contextId]);

    if (fetchRateMappingsQuery.loading || fetchRatesQuery.loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <CircularProgress color='secondary' />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div>{error}</div>
            </div>
        );
    }

    return (
        <RateMappingPage
            contextId={props.contextId}
            parameterId={props.parameterId}
            rates={rates}
            rateMappings={rateMappings}
            setRateMappings={setRateMappings}
            setMessage={props.setMessage}
            setContextAsDraft={props.setContextAsDraft}
        />
    )
}

const RateMappingPage = (props) => {
    const [mode, setMode] = useState({
        type: 'table', // table | view | add | edit
        rateMapping: null
    });

    const [deleteModal, setDeleteModal] = useState(null);

    const createRateMappingQuery = useExecution(createRateMapping);
    const updateRateMappingQuery = useExecution(updateRateMapping);
    const deleteRateMappingQuery = useExecution(deleteRateMapping);

    const formRef = useRef(null);

    if (mode.type === 'table') {
        return (
            <div>
                <LoadingDialog loading={deleteRateMappingQuery.loading} />
                <ConfirmDialog
                    open={deleteModal != null && !deleteRateMappingQuery.loading}
                    title={__t('confirm_delete')}
                    onClose={(action) => {
                        if (action != 'ok') {
                            setDeleteModal(null);
                            return;
                        }

                        deleteRateMappingQuery.execute(props.contextId, props.parameterId, deleteModal.id)
                            .then(() => {
                                props.setRateMappings(props.rateMappings.filter(rm => rm.id !== deleteModal.id));
                                setDeleteModal(null);

                                props.setContextAsDraft();
                            })
                            .catch(err => {
                                props.setMessage({ text: err || 'Unexpected error', isError: true });
                            });
                    }}
                />
                <TitleToolbar
                    title={__t('rate_mappings')}
                    onAdd={() => {
                        setMode({ type: 'add', rateMapping: null });
                    }}
                >
                    {props.rateMappings.map(rm =>
                        <ListItem
                            onView={() => {
                                setMode({ type: 'view', rateMapping: rm });
                            }}
                            onDelete={() => {
                                setDeleteModal(rm);
                            }}
                        >
                            <RateMappingPreview
                                fromDate={jsonDateStrToDisplay(rm.fromDate)}
                                toDate={jsonDateStrToDisplay(rm.toDate)}
                                rates={rm.rates.map(r => r.name).join(', ')}
                            />
                        </ListItem>
                    )}
                </TitleToolbar>
            </div>
        )
    }

    return (
        <div>
            <LoadingDialog loading={createRateMappingQuery.loading || updateRateMappingQuery.loading} />
            <TitleToolbar
                title={`${__t(mode.type)} ${__t('rate_mapping')}`}
                onBack={mode.type == 'view' && (() => {
                    setMode({ type: 'table', rateMapping: null });
                })}
                onEdit={mode.type == 'view' && (() => {
                    setMode({ type: 'edit', rateMapping: mode.rateMapping });
                })}
                onSave={(mode.type == 'edit' || mode.type == 'add') && (() => {
                    const formData = formRef.current.getData();
                    if (!formData)
                        return;

                    const rateMappingData = {
                        fromDate: toDateJsonString(formData.fromDate),
                        toDate: toDateJsonString(formData.toDate),
                        rateIds: formData.rates.map(r => r.id)
                    };

                    if (mode.type === 'add') {
                        createRateMappingQuery.execute(props.contextId, props.parameterId, rateMappingData)
                            .then((newRateMapping) => {
                                props.setRateMappings([...props.rateMappings, newRateMapping]);
                                setMode({ type: 'table', rateMapping: null });

                                props.setContextAsDraft();
                            })
                            .catch(err => {
                                props.setMessage({ text: err || 'Unexpected error', isError: true });
                            });
                    } else if (mode.type === 'edit') {
                        updateRateMappingQuery.execute(props.contextId, props.parameterId, mode.rateMapping.id, rateMappingData)
                            .then((updatedRateMapping) => {
                                props.setRateMappings(props.rateMappings.map(rm => rm.id === updatedRateMapping.id ? updatedRateMapping : rm));
                                setMode({ type: 'table', rateMapping: null });

                                props.setContextAsDraft();
                            })
                            .catch(err => {
                                props.setMessage({ text: err || 'Unexpected error', isError: true });
                            });
                    }
                })}
                onCancel={(mode.type == 'edit' || mode.type == 'add') && (() => {
                    if (mode.type === 'add') {
                        setMode({ type: 'table', rateMapping: null });
                        return;
                    }

                    setMode({ type: 'view', rateMapping: mode.rateMapping });
                })}
            >
                <RateMappingForm
                    key={'form-ratemapping-' + mode.type}
                    ref={formRef}
                    contextId={props.contextId}
                    allRates={props.rates}
                    rateMapping={mode.rateMapping}
                    readonly={mode.type === 'view'}
                />
            </TitleToolbar>
        </div >
    )
}

export default connect(
    () => ({}),
    (dispatch) => ({
        setContextAsDraft: () => {
            dispatch(contextsActions.setAsDraft());
        }
    }))(RateMappingController);
